import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, NgZone, provideAppInitializer } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Settings } from 'luxon';
import { interval, switchMap } from 'rxjs';
import { ServerClockService } from './server-clock.service';

/**
 * Ritmo a que o agendador acorda. É só a resolução do relógio de fundo — quem
 * decide se vale a pena gastar um pedido é o `ServerClockService.resyncOnSchedule`,
 * que conhece o recuo das tentativas e o intervalo de revalidação.
 */
export const SCHEDULE_TICK_MS = 60_000;

/**
 * Fetches the time on startup so the app's date guards stop depending on the
 * machine clock, and re-checks it whenever the tab comes back to the foreground.
 * See {@link ServerClockService} for what that buys.
 *
 * The bootstrap is deliberately **not** held for the first fetch. The sources
 * are third-party hosts, and blocking the first paint on one would put a foreign
 * network round-trip in front of every boot — including boots on the venue
 * networks most likely to block it. Until the answer lands (a few hundred ms)
 * the app reads the local clock, which is what it did before this existed, and
 * no date-bearing screen is reachable that fast.
 *
 * The `visibilitychange` hook covers the one case the monotonic counter cannot:
 * a machine suspended with the app open. `resyncIfDrifted` decides whether that
 * actually warrants a request, so a user flipping between tabs costs nothing.
 *
 * ## Porque é que um evento não chega
 *
 * O `visibilitychange` só dispara se alguém mudar de aba. Uma estação de balcão
 * em modo quiosque nunca o faz, e antes do agendador abaixo isso significava
 * duas coisas: uma falha de rede no arranque — o PC que abre o browser antes de
 * a rede associar, ou atrás de um portal cativo — nunca mais era tentada, e uma
 * âncora tirada há dias nunca era revalidada. O resultado era a app a correr o
 * dia inteiro no relógio da máquina, em silêncio (Sentry THE-NEW-R6).
 *
 * O temporizador corre FORA da zona do Angular: não mexe em nada que se
 * renderize, e dentro dela manteria o `ApplicationRef.isStable` permanentemente
 * falso e forçaria uma deteção de alterações a cada minuto, para nada.
 */
export function initializeServerClock(): void {
  const serverClock = inject(ServerClockService);
  const document = inject(DOCUMENT);
  const destroyRef = inject(DestroyRef);
  const ngZone = inject(NgZone);

  // Luxon's global clock hook — every `DateTime.now()` / `DateTime.local()` in
  // the app (and there are ~150 of them, most nowhere near DateTimeService) now
  // reads the synced time instead of the machine's. Installed before the first
  // fetch on purpose: `nowMillis()` answers with the local clock until something
  // is anchored, so this is never worse than what Luxon did on its own.
  //
  // Note this does NOT reach `new Date()` / `Date.now()`, which the codebase
  // also uses. Those stay on the machine clock — see the service docs.
  Settings.now = () => serverClock.nowMillis();

  serverClock.sync().subscribe();

  const onVisibilityChange = (): void => {
    if (document.visibilityState !== 'visible') return;
    serverClock.resyncIfDrifted().subscribe();
  };

  document.addEventListener('visibilitychange', onVisibilityChange);
  destroyRef.onDestroy(() =>
    document.removeEventListener('visibilitychange', onVisibilityChange),
  );

  ngZone.runOutsideAngular(() => {
    interval(SCHEDULE_TICK_MS)
      .pipe(
        switchMap(() => serverClock.resyncOnSchedule()),
        takeUntilDestroyed(destroyRef),
      )
      .subscribe();
  });
}

/**
 * Provider for server-clock initialization, shared by every app shell.
 */
export const SERVER_CLOCK_INITIALIZER = provideAppInitializer(
  initializeServerClock,
);
